import { mergeAttributes, Node } from '@tiptap/core'
import { formatTokenText } from '@/lib/templateVariables'

export interface PersonalizationTokenOptions {
  HTMLAttributes: Record<string, string>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    personalizationToken: {
      insertToken: (attrs: { key: string; label: string; fallback?: string }) => ReturnType
    }
  }
}

export const PersonalizationToken = Node.create<PersonalizationTokenOptions>({
  name: 'personalizationToken',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  // Nessuna `marks` whitelist: su un atom governerebbe i mark del contenuto (che
  // non c'è), non quelli applicati sopra. I mark che avvolgono il chip arrivano
  // dal payload di `insertToken` (vedi addCommands).

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      key: { default: null },
      label: { default: null },
      fallback: { default: null },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-token-key]',
        getAttrs: (node) => {
          const el = node as HTMLElement
          return {
            key: el.getAttribute('data-token-key'),
            label: el.getAttribute('data-token-label'),
            fallback: el.getAttribute('data-token-fallback'),
          }
        },
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const { key, label, fallback } = node.attrs
    const attrs: Record<string, string> = {
      'data-token-key': key,
      'data-token-label': label || key,
      class: 'personalization-token',
    }
    if (fallback) {
      attrs['data-token-fallback'] = fallback
    }

    const displayText = fallback ? `${label || key} | '${fallback}'` : (label || key)

    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, attrs),
      displayText,
    ]
  },

  renderText({ node }) {
    const { key, fallback } = node.attrs
    return formatTokenText(key, fallback)
  },

  addCommands() {
    return {
      insertToken:
        (attrs) =>
        ({ state, commands }) => {
          // Eredita lo stile attivo al cursore: il {{token}} serializzato
          // resta avvolto dai mark del testo circostante.
          const marks = state.storedMarks ?? state.selection.$from.marks()
          return commands.insertContent({
            type: this.name,
            attrs,
            marks: marks.map((mark) => ({
              type: mark.type.name,
              attrs: mark.attrs,
            })),
          })
        },
    }
  },
})
